// ============================================
// DeferredLightingPipeline — Orchestrates the 2D deferred lighting path
// ============================================
//
// GBuffer Pass:
//   Sprites render to MRT (albedo, normal, emission) via SpriteBatch.beginGBuffer()
//
// Light Accumulation Pass:
//   Fullscreen triangle reads GBuffer textures + light storage/uniform buffers
//   → outputs fully lit color to a target (backbuffer or HDR offscreen)
//
// Usage:
//   1. pipeline.beginGBufferPass()        — bind & clear GBuffer
//   2. spriteBatch.beginGBuffer(camera)   — draw sprites to GBuffer
//   3. spriteBatch.end()
//   4. pipeline.renderAccumulation(mgr, target, hdr) — fullscreen lighting
//   5. ... render overlays/gizmos on top

import { GBufferTarget } from './GBufferTarget';
import { LightManager } from '../lighting/LightManager';
import type { IRenderContext } from '../core/IRenderContext';
import type { WebGPURenderContext } from '../core/WebGPURenderContext';
import type { RenderTargetHandle, BufferHandle } from '../types';
import { NULL_HANDLE } from '../types';
import { getCompiledShader } from './ShaderCache';

// ============================================================================
// DEFERRED LIGHTING PIPELINE
// ============================================================================

export class DeferredLightingPipeline {
  private ctx: IRenderContext;
  private device: GPUDevice;
  private gbuffer: GBufferTarget;

  // Accumulation pass (raw WebGPU — uses storage buffer, custom bind group)
  private accumModule: GPUShaderModule | null = null;
  private accumPipelineLDR: GPURenderPipeline | null = null;
  private accumPipelineHDR: GPURenderPipeline | null = null;
  private accumBindGroupLayout: GPUBindGroupLayout | null = null;
  private accumSampler: GPUSampler | null = null;

  // Bind group cache (avoid per-frame allocation)
  private accumBindGroup: GPUBindGroup | null = null;
  private accumBindGroupDirty = true;
  private lastLightBuf: GPUBuffer | null = null;
  private lastUniformBuf: GPUBuffer | null = null;
  private lastTileBuf: GPUBuffer | null = null;
  private lastShadowBuf: GPUBuffer | null = null;
  private lastIESBuf: GPUBuffer | null = null;
  private _dummyShadowBuffer: GPUBuffer | null = null;
  private _dummyIESBuffer: GPUBuffer | null = null;

  private _width: number;
  private _height: number;

  constructor(ctx: IRenderContext, width: number, height: number) {
    this.ctx = ctx;
    const device = ctx.getDevice?.();
    if (!device) throw new Error('[DeferredLightingPipeline] Requires WebGPU device');
    this.device = device;
    this._width = width;
    this._height = height;

    // Create GBuffer (3-attachment MRT: albedo, normal, emission + depth for volumetric)
    this.gbuffer = new GBufferTarget(ctx, width, height, true);

    // Build accumulation pipeline
    this.initAccumulation();
  }

  // ========================================================================
  // PUBLIC API
  // ========================================================================

  /** The underlying GBufferTarget (for advanced use / debugging). */
  get gbufferTarget(): GBufferTarget { return this.gbuffer; }

  get width(): number { return this._width; }
  get height(): number { return this._height; }

  /**
   * Begin the GBuffer write pass.
   * Binds the GBuffer MRT as the active render target and clears it.
   * After this, use SpriteBatch.beginGBuffer(camera) to draw sprites.
   *
   * @param ctx — The render context to bind the MRT on. Must be the same
   *              context the SpriteBatch will draw to.
   */
  beginGBufferPass(ctx: IRenderContext): void {
    ctx.setMRTRenderTarget(this.gbuffer.mrtHandle);
    ctx.clear(0, 0, 0, 0);
  }

  /**
   * Run the light accumulation fullscreen pass.
   *
   * Reads GBuffer textures + LightManager buffers → outputs fully lit color.
   * This method flushes pending ctx commands, then creates its own
   * command encoder for the accumulation pass.
   *
   * @param lightManager — LightManager with uploaded light data (after end())
   * @param outputTarget — null for backbuffer, or RenderTargetHandle for HDR offscreen
   * @param hdr — true if outputTarget is RGBA16Float
   * @param ctx — The render context to flush before the accumulation pass.
   *              Must match the context that rendered to the GBuffer.
   * @param tileLightBuffer — optional Forward+ tile light data buffer from LightCullPass
   * @param shadowMapBuffer — optional shadow atlas buffer from ShadowMapPass
   * @param iesProfileBuffer — optional IES profile data buffer from IESProfileManager
   */
  renderAccumulation(
    lightManager: LightManager,
    outputTarget: RenderTargetHandle | null,
    hdr: boolean,
    ctx: IRenderContext,
    tileLightBuffer?: BufferHandle,
    shadowMapBuffer?: BufferHandle,
    iesProfileBuffer?: BufferHandle,
  ): void {
    if (!this.accumPipelineLDR || !this.accumPipelineHDR || !this.accumBindGroupLayout) {
      return;
    }

    // ── Flush pending ctx commands so GBuffer contents are committed ──
    if (ctx.flushCompute) {
      ctx.flushCompute();
    }

    // ── Resolve GPU resources from opaque handles ──
    const maps = (this.ctx as WebGPURenderContext).getResourceMaps();

    const albedoRes = maps.textures.get(this.gbuffer.albedoTexture);
    const normalRes = maps.textures.get(this.gbuffer.normalTexture);
    const emissionRes = maps.textures.get(this.gbuffer.emissionTexture);
    if (!albedoRes || !normalRes || !emissionRes) return;

    const lightBuf = maps.buffers.get(lightManager.lightBuffer);
    const uniformBuf = maps.buffers.get(lightManager.uniformBuffer);
    if (!lightBuf || !uniformBuf) return;

    // Resolve tile light data buffer (Forward+ culling result)
    const tileBuf = tileLightBuffer != null ? maps.buffers.get(tileLightBuffer) : null;
    if (!tileBuf) return; // tile data is required for tile-culled accumulation

    // Resolve shadow map buffer (optional — use dummy if not provided)
    let shadowBuf: GPUBuffer;
    if (shadowMapBuffer != null) {
      const resolved = maps.buffers.get(shadowMapBuffer);
      shadowBuf = resolved ? resolved.gpuBuffer : this.getDummyShadowBuffer();
    } else {
      shadowBuf = this.getDummyShadowBuffer();
    }

    // Resolve IES profile buffer (optional — use dummy if not provided)
    let iesBuf: GPUBuffer;
    if (iesProfileBuffer != null) {
      const resolved = maps.buffers.get(iesProfileBuffer);
      iesBuf = resolved ? resolved.gpuBuffer : this.getDummyIESBuffer();
    } else {
      iesBuf = this.getDummyIESBuffer();
    }

    // ── Determine output view ──
    let outputView: GPUTextureView;
    if (outputTarget === null || outputTarget === NULL_HANDLE) {
      // Backbuffer — use the CALLER's canvas, not the stored one
      const canvasCtx = ctx.canvas.getContext('webgpu') as GPUCanvasContext;
      outputView = canvasCtx.getCurrentTexture().createView();
    } else {
      const rt = maps.renderTargets.get(outputTarget);
      if (!rt) return;
      outputView = rt.colorView;
    }

    // ── Create or reuse bind group (cache to avoid per-frame allocation) ──
    const needsRebuild = this.accumBindGroupDirty
      || !this.accumBindGroup
      || this.lastLightBuf !== lightBuf.gpuBuffer
      || this.lastUniformBuf !== uniformBuf.gpuBuffer
      || this.lastTileBuf !== tileBuf.gpuBuffer
      || this.lastShadowBuf !== shadowBuf
      || this.lastIESBuf !== iesBuf;

    if (needsRebuild) {
      this.accumBindGroup = this.device.createBindGroup({
        layout: this.accumBindGroupLayout,
        entries: [
          { binding: 0, resource: { buffer: uniformBuf.gpuBuffer } },
          { binding: 1, resource: { buffer: lightBuf.gpuBuffer } },
          { binding: 2, resource: albedoRes.gpuView },
          { binding: 3, resource: normalRes.gpuView },
          { binding: 4, resource: emissionRes.gpuView },
          { binding: 5, resource: this.accumSampler! },
          { binding: 6, resource: { buffer: tileBuf.gpuBuffer } },
          { binding: 7, resource: { buffer: shadowBuf } },
          { binding: 8, resource: { buffer: iesBuf } },
        ],
      });
      this.lastLightBuf = lightBuf.gpuBuffer;
      this.lastUniformBuf = uniformBuf.gpuBuffer;
      this.lastTileBuf = tileBuf.gpuBuffer;
      this.lastShadowBuf = shadowBuf;
      this.lastIESBuf = iesBuf;
      this.accumBindGroupDirty = false;
    }

    const bindGroup = this.accumBindGroup!;

    // ── Encode and submit accumulation pass ──
    const encoder = this.device.createCommandEncoder({ label: 'deferred-light-accum' });

    const pass = encoder.beginRenderPass({
      colorAttachments: [{
        view: outputView,
        loadOp: 'load',   // preserve existing content (background color, grid, etc.)
        storeOp: 'store',
      }],
      label: 'light-accumulation-pass',
    });

    pass.setPipeline(hdr ? this.accumPipelineHDR : this.accumPipelineLDR);
    pass.setBindGroup(0, bindGroup);
    pass.draw(3); // fullscreen triangle (generated in vertex shader)
    pass.end();

    this.device.queue.submit([encoder.finish()]);
  }

  /**
   * Resize the GBuffer (e.g. on viewport resize).
   */
  resize(width: number, height: number): void {
    if (width === this._width && height === this._height) return;
    this._width = width;
    this._height = height;
    this.gbuffer.resize(width, height);
    this.accumBindGroupDirty = true;
  }

  /**
   * Release all GPU resources.
   */
  destroy(): void {
    this.gbuffer.destroy();
    // Raw WebGPU pipelines are GC'd by the device — no manual destroy needed
    this.accumPipelineLDR = null;
    this.accumPipelineHDR = null;
    this.accumModule = null;
    this.accumBindGroupLayout = null;
    this.accumSampler = null;
    this._dummyShadowBuffer?.destroy();
    this._dummyShadowBuffer = null;
    this._dummyIESBuffer?.destroy();
    this._dummyIESBuffer = null;
  }

  // ========================================================================
  // INTERNALS
  // ========================================================================

  /** Get or create a tiny dummy buffer for shadow map binding when no shadows exist. */
  private getDummyShadowBuffer(): GPUBuffer {
    if (!this._dummyShadowBuffer) {
      this._dummyShadowBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.STORAGE,
        label: 'dummy-shadow-buffer-accum',
      });
    }
    return this._dummyShadowBuffer;
  }

  /** Get or create a tiny dummy buffer for IES profile binding when no IES profiles exist. */
  private getDummyIESBuffer(): GPUBuffer {
    if (!this._dummyIESBuffer) {
      // Minimum size: 1 profile × IES_PROFILE_RESOLUTION samples × 4 bytes per f32
      // We use a single-element buffer with value 1.0 (no IES modulation)
      this._dummyIESBuffer = this.device.createBuffer({
        size: 4,
        usage: GPUBufferUsage.STORAGE,
        label: 'dummy-ies-buffer-accum',
      });
    }
    return this._dummyIESBuffer;
  }

  /**
   * Build the light accumulation render pipeline (raw WebGPU).
   * Uses a custom bind group layout with uniform + storage + 3 textures + sampler.
   */
  private initAccumulation(): void {
    // ── Precompiled Slang → WGSL from the startup cache ──
    this.accumModule = this.device.createShaderModule({
      code: getCompiledShader('lighting/light-accumulation'),
      label: 'light-accumulation',
    });

    // ── Bind group layout: matches light-accumulation.wgsl bindings ──
    this.accumBindGroupLayout = this.device.createBindGroupLayout({
      label: 'light-accum-bgl',
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 4, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
        { binding: 5, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
        { binding: 6, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
        { binding: 7, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
        { binding: 8, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'read-only-storage' } },
      ],
    });

    const pipelineLayout = this.device.createPipelineLayout({
      bindGroupLayouts: [this.accumBindGroupLayout],
      label: 'light-accum-layout',
    });

    // ── Sampler for GBuffer texture reads ──
    this.accumSampler = this.device.createSampler({
      minFilter: 'linear',
      magFilter: 'linear',
      label: 'light-accum-sampler',
    });

    // ── Alpha blending for compositing lit sprites over background ──
    const blendState: GPUBlendState = {
      color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha', operation: 'add' },
      alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
    };

    // ── LDR pipeline (swap chain format) ──
    const swapFormat = this.ctx.getSwapChainFormat() as GPUTextureFormat;
    // Tile/shadow/IES config now lives in uLighting.fpConfig (uniform), not pipeline overrides.

    this.accumPipelineLDR = this.device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: { module: this.accumModule, entryPoint: 'vs_main' },
      fragment: {
        module: this.accumModule,
        entryPoint: 'fs_main',
        targets: [{ format: swapFormat, blend: blendState }],
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      label: 'light-accum-ldr',
    });

    // ── HDR pipeline (rgba16float) ──
    this.accumPipelineHDR = this.device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: { module: this.accumModule, entryPoint: 'vs_main' },
      fragment: {
        module: this.accumModule,
        entryPoint: 'fs_main',
        targets: [{ format: 'rgba16float', blend: blendState }],
      },
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      label: 'light-accum-hdr',
    });
  }
}
